// components/Header/Header.tsx
import React from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { useClients } from "@/domains/Client/useClients";
import { ClientSelector } from "./ClientSelector";
import { UserMenu } from "./UserMenu";
import { NotificationBell } from "./NotificationBell";
import { TabGroup, TabItem, TabGroupConfig } from "../ui/TabGroup";
import { AddNotePopover } from "@/domains/Home/components/AddNotePopover";
import { ChevronLeftIcon } from "@/components/icons";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { BreadcrumbTrail } from "./BreadcrumbTrail";
import { PhoneNavSheet } from "./PhoneNavSheet";

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams<{ clientId: string }>();
  const { user, selectedClient, setActiveModal, setCurrentView } =
    useAppStore();
  const { clients, isLoading, addClient } = useClients();

  // Check if we're in the client workspace (has clientId in URL)
  const isInWorkspace = !!clientId;

  const handleImageEditor = () => {
    setActiveModal("image-editor");
  };

  const handleNavigation = (
    view: "home" | "grid" | "library" | "posts" | "stories" | "reporting" | "ads" | "finance",
    path: string,
  ) => {
    setCurrentView(view);
    // Navigate relative to client workspace
    const fullPath = clientId ? `/app/${clientId}${path}` : path;
    navigate(fullPath);
  };

  const isActive = (path: string) => {
    if (clientId) {
      return location.pathname === `/app/${clientId}${path}`;
    }
    return location.pathname === path;
  };

  // Define navigation tabs. "setup" points at the CLIENT settings page
  // inside the workspace; agency-wide settings stay reachable at /setup
  // directly (not via this nav).
  const navEntities = ["home", "idea", "brief", "media", "story", "post", "grid", "ads", "reporting", "finance", "setup"] as const;
  const navRoutes: Record<string, string> = {
    home: "/home",
    idea: "/ideas",
    brief: "/briefs",
    media: "/library",
    story: "/stories",
    post: "/posts",
    grid: "/grid",
    ads: "/ads",
    finance: "/finance",
    reporting: "/reporting",
    setup: "/settings",
  };
  // A client's Finance tab is open to every admin (the user 2026-10-30).
  const navigationTabs: TabItem[] = navEntities
    .map((key) => {
    const entity = ENTITY_CONFIG[key];
    const Icon = entity.icon;
    return {
      id: navRoutes[key],
      label: entity.plural,
      icon: <Icon className="w-4 h-4" />,
    };
  });

  const navigationConfig: TabGroupConfig = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    hasBackground: true,
    labelClassName: "hidden xl:inline",
  };

  const handleTabChange = (tabId: string) => {
    if (tabId === "editor") {
      handleImageEditor();
    } else if (tabId === "/settings") {
      // Client settings page; fall back to agency setup with no client.
      navigate(clientId ? `/app/${clientId}/settings` : "/setup");
    } else if (tabId === "/library") {
      // Navigate to /library/photos by default
      setCurrentView("library");
      const fullPath = clientId ? `/app/${clientId}/library/photos` : "/library/photos";
      navigate(fullPath);
    } else {
      const viewMap: {
        [key: string]:
          | "home"
          | "grid"
          | "library"
          | "posts"
          | "stories"
          | "reporting"
          | "ideas"
          | "briefs"
          | "ads"
          | "finance";
      } = {
        "/home": "home",
        "/grid": "grid",
        "/stories": "stories",
        "/posts": "posts",
        "/reporting": "reporting",
        "/ideas": "ideas",
        "/briefs": "briefs",
        "/ads": "ads",
        "/finance": "finance",
      };
      const view = viewMap[tabId];
      if (view) {
        handleNavigation(view as any, tabId);
      }
    }
  };

  // Determine active tab based on current path
  const getActiveTab = () => {
    const path = location.pathname;
    // Agency setup lives outside the client workspace — it highlights the
    // same Setup tab. Match before the /app/{clientId} stripping below.
    if (path === '/setup' || path.startsWith('/setup/')) return '/settings';
    if (clientId) {
      const relativePath = path.replace(`/app/${clientId}`, '');
      if (relativePath === '' || relativePath === '/' || relativePath.startsWith('/home')) return '/home';
      if (relativePath === '/grid') return '/grid';
      if (relativePath.startsWith('/library')) return '/library';
      if (relativePath.startsWith('/stories')) return '/stories';
      if (relativePath.startsWith('/posts')) return '/posts';
      if (relativePath === '/reporting') return '/reporting';
      if (relativePath.startsWith('/ideas')) return '/ideas';
      if (relativePath.startsWith('/briefs')) return '/briefs';
      if (relativePath.startsWith('/ads')) return '/ads';
      if (relativePath.startsWith('/finance')) return '/finance';
      if (relativePath.startsWith('/settings')) return '/settings';
    }
    return path;
  };

  return (
    <>
      <header>
        <div className="px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left side: below md a burger opens the section sheet, with
                Dashboard as its first row (DD-057); from md up, the
                Dashboard link and the tab strip. Like the desktop Dashboard
                link, the burger shows while the client is still loading, with
                no section rows until it arrives, so a phone can always get
                back to the agency. */}
            <div className="flex items-center gap-4 min-w-0">
              {user && (
                <PhoneNavSheet
                  title={selectedClient?.clientName ?? "Menu"}
                  items={selectedClient ? navigationTabs : []}
                  activeId={getActiveTab()}
                  onSelect={handleTabChange}
                  backLink={isInWorkspace ? { label: "Dashboard", to: "/" } : undefined}
                />
              )}
              <div className="hidden md:flex items-center gap-4 min-w-0 overflow-x-auto">
                {/* Dashboard back link (only in workspace) */}
                {isInWorkspace && user && (
                  <Link
                    to="/"
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">Dashboard</span>
                  </Link>
                )}

                {/* Divider */}
                {isInWorkspace && user && selectedClient && (
                  <div className="h-6 w-px bg-gray-200" />
                )}

                {/* Navigation tabs */}
                {user && selectedClient && (
                  <TabGroup
                    items={navigationTabs}
                    activeId={getActiveTab()}
                    onTabChange={handleTabChange}
                    config={navigationConfig}
                  />
                )}
              </div>
            </div>

            {/* Right side - Client selector and user */}
            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              {user && (
                <>
                  {selectedClient && (
                    <AddNotePopover clientId={selectedClient.id} />
                  )}
                  {/* Bell shows agency-level notifications by default;
                      filters to the selected client when one is active. */}
                  <NotificationBell clientId={selectedClient?.id ?? null} />
                  <ClientSelector
                    clients={clients}
                    selectedClient={selectedClient}
                    isLoading={isLoading}
                    onClientCreated={addClient}
                  />
                  <UserMenu user={user} />
                </>
              )}
            </div>
          </div>
          <BreadcrumbTrail />
        </div>
      </header>
    </>
  );
};
